import { useEffect, useRef, useState } from "react";
import { Button, Col, Row, Stack } from "react-bootstrap";
import { CaretLeftFill, CaretRightFill } from "react-bootstrap-icons";
import ScreenshotButton from "../ScreenshotButton";
import ClocksAudioCard from "./ClocksAudioCard";
import ClocksChart from "./ClocksChart";
import ClocksContentBalanceCard from "./ClocksContentBalanceCard";
import ClocksRatingsCard from "./ClocksRatingsCard";
import ClocksSessionsCard from "./ClocksSessionsCard";
import { useClocksContentBalanceData, useClocksContentData, useClocksData, useClocksRatingsData, useClocksSessionsData } from "./clocksHooks";
import DataDescription from "../DataDescription";

export default function ClocksData({
    radioId,
    demo,
    dataType,
    date,
    hour,
    refreshCounter,
    setMainData,
    setExcludedDays,
    nextHour,
    previousHour,
    nextDay,
    previousDay,
    addToCollectionAllowed,
    secondary = false,
    startAudioTime = 0,
}) {
    const { loading, errorCode, data, getData } = useClocksData();
    const { loading: ratingsLoading, errorCode: ratingsErrorCode, data: ratingsData, getData: getRatingsData } = useClocksRatingsData();
    const { loading: sessionLoading, errorCode: sessionErrorCode, data: sessionData, getData: getSessionData } = useClocksSessionsData();
    const { loading: balanceLoading, errorCode: balanceErrorCode, data: balanceData, getData: getBalanceData } = useClocksContentBalanceData();
    const { loading: balanceAvgLoading, data: balanceAvgData, getData: getBalanceAvgData } = useClocksContentBalanceData();
    const { data: contentData, getData: getContentData, setData: setContentData } = useClocksContentData();

    const secondaryContainerRef = useRef(null);

    const [isAudioAvailable, setIsAudioAvailable] = useState(false);
    const [showRatingsLabels, setShowRatingsLabels] = useState(5);
    const [currentAudioTime, setCurrentAudioTime] = useState(startAudioTime);
    const audioRef = useRef(null);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [isPlaying, setIsPlaying] = useState(false);

    const [markInSeconds, setMarkInSeconds] = useState(0);
    const [markOutSeconds, setMarkOutSeconds] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        const fetchData = async () => {
            await Promise.all([
                getData(radioId, date, hour, controller),
                getRatingsData(radioId, demo, date, hour, controller),
                getSessionData(radioId, demo[0]?.demo_id, date, hour, false, controller),
                getBalanceData(radioId, date, hour, false, controller),
                getBalanceAvgData(radioId, date, hour, true, controller),
                getContentData(radioId, date, hour, controller),
            ]);
        };
        if (radioId && date) fetchData();

        return () => {
            controller.abort();
        };
    }, [radioId, demo, date, hour, getData, getRatingsData, refreshCounter, getSessionData, getBalanceData, getBalanceAvgData, getContentData]);

    useEffect(() => {
        if (setMainData) {
            setMainData(data);
        }
    }, [data, setMainData]);

    useEffect(() => {
        if (setExcludedDays) {
            setExcludedDays(ratingsData?.data[0]?.excluded_days);
        }
    }, [ratingsData, setExcludedDays]);

    if (!data) return null;

    return (
        <Row className=" flex-grow-1 gx-1" ref={secondaryContainerRef}>
            {secondary ? (
                <Col xs={12} className=" order-first">
                    <Row className="gx-1 justify-content-center">
                        <Col xs={12}>
                            <Stack direction="horizontal" gap={1}>
                                <DataDescription>
                                    {(data?.aa_status === 4 || data?.aa_status === -11) && (
                                        <DataDescription.Simple label="AA" value={data.aa_status === 4 ? "Processed" : "Processing"} />
                                    )}
                                    <DataDescription.ExcludedDays excludedDays={ratingsData?.data[0]?.excluded_days} />
                                </DataDescription>
                                <div className="ms-auto">
                                    <ScreenshotButton disabled={!data} fileName={"clocks"} container={secondaryContainerRef} />
                                </div>
                            </Stack>
                        </Col>
                        <Col xs={12} xl={6} className="d-none d-xl-block">
                            <ClocksAudioCard
                                data={data}
                                isPlaying={isPlaying}
                                setIsPlaying={setIsPlaying}
                                currentAudioTime={currentAudioTime}
                                setCurrentAudioTime={setCurrentAudioTime}
                                audioRef={audioRef}
                                isAudioAvailable={isAudioAvailable}
                                setIsAudioAvailable={setIsAudioAvailable}
                                playbackRate={playbackRate}
                                setPlaybackRate={setPlaybackRate}
                                refreshCounter={refreshCounter}
                                markInSeconds={markInSeconds}
                                markOutSeconds={markOutSeconds}
                            />
                        </Col>
                        {ratingsData && (
                            <Col xs={12} xl={6}>
                                <ClocksRatingsCard data={ratingsData} showRatingsLabels={showRatingsLabels} setShowRatingsLabels={setShowRatingsLabels} />
                            </Col>
                        )}
                    </Row>
                </Col>
            ) : (
                <Col xs={12} xl={"auto"} className="clocks-side">
                    <Stack gap={3}>
                        <div className=" d-none d-xl-block">
                            <ClocksAudioCard
                                data={data}
                                isPlaying={isPlaying}
                                setIsPlaying={setIsPlaying}
                                currentAudioTime={currentAudioTime}
                                setCurrentAudioTime={setCurrentAudioTime}
                                audioRef={audioRef}
                                isAudioAvailable={isAudioAvailable}
                                setIsAudioAvailable={setIsAudioAvailable}
                                playbackRate={playbackRate}
                                setPlaybackRate={setPlaybackRate}
                                refreshCounter={refreshCounter}
                                markInSeconds={markInSeconds}
                                markOutSeconds={markOutSeconds}
                            />
                        </div>
                        <Stack direction="horizontal" className="order-first d-xl-none mb-3" gap={2}>
                            <Button size="sm" className=" btn-ra-yellow d-flex align-items-center fs-8" onClick={previousDay}>
                                <CaretLeftFill width={20} height={20} /> Day
                            </Button>
                            <Button size="sm" className=" btn-ra-yellow d-flex align-items-center fs-8" onClick={async () => await previousHour()}>
                                <CaretLeftFill width={20} height={20} /> Hour
                            </Button>
                            <Button size="sm" className=" btn-ra-yellow d-flex align-items-center fs-8 ms-auto" onClick={async () => await nextHour()}>
                                Hour <CaretRightFill width={20} height={20} />
                            </Button>
                            <Button size="sm" className=" btn-ra-yellow d-flex align-items-center fs-8" onClick={nextDay}>
                                Day <CaretRightFill width={20} height={20} />
                            </Button>
                        </Stack>
                        <ClocksRatingsCard
                            data={ratingsData}
                            ratingsLoading={ratingsLoading}
                            error={ratingsErrorCode}
                            showRatingsLabels={showRatingsLabels}
                            setShowRatingsLabels={setShowRatingsLabels}
                        />
                        <ClocksSessionsCard data={sessionData} dataLoading={sessionLoading} error={sessionErrorCode} />
                        <ClocksContentBalanceCard
                            data={balanceData}
                            avgData={balanceAvgData}
                            dataLoading={balanceLoading}
                            avgDataLoading={balanceAvgLoading}
                            error={balanceErrorCode}
                        />
                    </Stack>
                </Col>
            )}
            <Col xs={12} className="d-xl-none order-first">
                <ClocksAudioCard
                    data={data}
                    isPlaying={isPlaying}
                    setIsPlaying={setIsPlaying}
                    currentAudioTime={currentAudioTime}
                    setCurrentAudioTime={setCurrentAudioTime}
                    audioRef={audioRef}
                    isAudioAvailable={isAudioAvailable}
                    setIsAudioAvailable={setIsAudioAvailable}
                    playbackRate={playbackRate}
                    setPlaybackRate={setPlaybackRate}
                    refreshCounter={refreshCounter}
                    markInSeconds={markInSeconds}
                    markOutSeconds={markOutSeconds}
                />
            </Col>
            <Col xs={12} xl className=" order-first order-xl-last">
                <ClocksChart
                    data={data}
                    dataType={dataType}
                    ratingsData={ratingsData}
                    contentData={contentData}
                    setContentData={setContentData}
                    loading={loading}
                    error={errorCode}
                    currentAudioTime={currentAudioTime}
                    showRatingsLabels={showRatingsLabels}
                    audioRef={audioRef}
                    isAudioAvailable={isAudioAvailable}
                    setIsPlaying={setIsPlaying}
                    setPlaybackRate={setPlaybackRate}
                    addToCollectionAllowed={addToCollectionAllowed}
                    markInSeconds={markInSeconds}
                    markOutSeconds={markOutSeconds}
                    setMarkInSeconds={setMarkInSeconds}
                    setMarkOutSeconds={setMarkOutSeconds}
                />
            </Col>
        </Row>
    );
}
